import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalPortal } from '@/components';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import Close from '@/assets/lightboxClose.svg';

const DEDGE_WIDGET_STYLE_ID = 'dedge-widget-style';
const DEDGE_WIDGET_SCRIPT_ID = 'dedge-widget-script';

const getBookingWidgetEmbedData = (embedCode) => {
  const fallbackEmbedData = {
    configJson: '{}',
    styleSrc: '',
    scriptSrc: '',
  };

  if (embedCode == null) {
    return fallbackEmbedData;
  }

  if (typeof embedCode === 'object') {
    try {
      return {
        ...fallbackEmbedData,
        configJson: JSON.stringify(embedCode),
      };
    } catch {
      return fallbackEmbedData;
    }
  }

  if (typeof embedCode !== 'string') {
    return fallbackEmbedData;
  }

  const trimmedEmbedCode = embedCode.trim();
  if (!trimmedEmbedCode) {
    return fallbackEmbedData;
  }

  const styleSrcMatch = trimmedEmbedCode.match(/<link[^>]*href=["']([^"']+)["'][^>]*>/i);
  const scriptSrcMatch = trimmedEmbedCode.match(/<script[^>]*src=["']([^"']+)["'][^>]*><\/script>/i);

  const configScriptMatch = trimmedEmbedCode.match(
    /<script[^>]*class=["'][^"']*fb-widget-config[^"']*["'][^>]*>([\s\S]*?)<\/script>/i,
  );
  const configPayload = (configScriptMatch?.[1] || trimmedEmbedCode).trim();

  if (!configPayload) {
    return {
      ...fallbackEmbedData,
      styleSrc: styleSrcMatch?.[1] || '',
      scriptSrc: scriptSrcMatch?.[1] || '',
    };
  }

  try {
    return {
      configJson: JSON.stringify(JSON.parse(configPayload)),
      styleSrc: styleSrcMatch?.[1] || '',
      scriptSrc: scriptSrcMatch?.[1] || '',
    };
  } catch {
    return {
      ...fallbackEmbedData,
      styleSrc: styleSrcMatch?.[1] || '',
      scriptSrc: scriptSrcMatch?.[1] || '',
    };
  }
};

const ensureStyleLoaded = (styleSrc) => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!styleSrc) {
    return Promise.reject(new Error('Failed to load D-EDGE booking stylesheet.'));
  }

  const existingStyle = document.getElementById(DEDGE_WIDGET_STYLE_ID);
  if (existingStyle) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const style = document.createElement('link');
    style.id = DEDGE_WIDGET_STYLE_ID;
    style.rel = 'stylesheet';
    style.href = styleSrc;
    style.onload = () => resolve();
    style.onerror = () => reject(new Error('Failed to load D-EDGE booking stylesheet.'));
    document.head.appendChild(style);
  });
};

const ensureScriptLoaded = (scriptSrc) => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!scriptSrc) {
    return Promise.reject(new Error('Failed to load D-EDGE booking script.'));
  }

  const existingScript = document.getElementById(DEDGE_WIDGET_SCRIPT_ID);
  if (existingScript) {
    existingScript.remove();
  }

  window.__dedgeWidgetScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = DEDGE_WIDGET_SCRIPT_ID;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      window.__dedgeWidgetScriptPromise = null;
      reject(new Error('Failed to load D-EDGE booking script.'));
    };
    document.body.appendChild(script);
  });

  return window.__dedgeWidgetScriptPromise;
};

const triggerWidgetInit = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  const initCandidates = [
    window.fbWidget?.init,
    window.FBWidget?.init,
    window.DEdgeWidget?.init,
    window.DEDGEWidget?.init,
    window.dEdgeWidget?.init,
    window.websdk?.widgets?.init,
  ];

  for (const initFn of initCandidates) {
    if (typeof initFn === 'function') {
      await initFn();
      return;
    }
  }
};

const cleanWidgetMarkup = (widgetConfigId) => {
  if (typeof window === 'undefined') {
    return;
  }

  const widgetNode = document.getElementById(`fb-widget-${widgetConfigId}`);
  if (widgetNode) {
    widgetNode.innerHTML = '';
  }
};

const DEDGEEmbedModule = ({ mainOpen, solidBG, isDark, navColor, className, isMobileButton, data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [widgetOpenCount, setWidgetOpenCount] = useState(0);
  const [isLoadingWidget, setIsLoadingWidget] = useState(false);
  const [widgetError, setWidgetError] = useState('');
  const openCountRef = useRef(0);

  const bookingWidgetEmbedData = useMemo(() => {
    const embedCode = data?.dEdgeEmbedCode;
    return getBookingWidgetEmbedData(embedCode);
  }, [data]);

  const bookingWidgetConfigJson = bookingWidgetEmbedData.configJson;

  const widgetConfigId = useMemo(() => String(widgetOpenCount), [widgetOpenCount]);

  const openModal = useCallback(() => {
    openCountRef.current += 1;
    setWidgetOpenCount(openCountRef.current);
    setWidgetError('');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsLoadingWidget(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isModalOpen || !widgetOpenCount) {
      return undefined;
    }

    const initWidget = async () => {
      setIsLoadingWidget(true);

      // Wait until modal content and widget container are mounted in the DOM.
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => resolve());
        });
      });

      try {
        cleanWidgetMarkup(widgetConfigId);
        await ensureStyleLoaded(bookingWidgetEmbedData.styleSrc);
        await ensureScriptLoaded(bookingWidgetEmbedData.scriptSrc);
        await triggerWidgetInit();

        if (!cancelled) {
          setIsLoadingWidget(false);
        }
      } catch (error) {
        if (!cancelled) {
          setWidgetError(error?.message || 'Unable to load booking widget.');
          setIsLoadingWidget(false);
        }
      }
    };

    initWidget();

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, widgetOpenCount, widgetConfigId, bookingWidgetEmbedData.styleSrc, bookingWidgetEmbedData.scriptSrc]);

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={classNames(
          isMobileButton
            ? '!pl-[14px] !pt-6'
            : 'btn secondary openEdgeWidget dark ml-3 px-3 duration-700 group-hover:border-charcoal group-hover:bg-charcoal sm:px-6',
          (solidBG || mainOpen) && 'border-charcoal bg-charcoal text-charcoal',
          !isDark && !navColor && !solidBG ? '!border-charcoal bg-charcoal !text-silk' : '',
          className,
        )}
      >
        {data.text || 'Book Now'}
      </button>

      <ModalPortal>
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              key={`dedge-modal-${widgetOpenCount}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className="disable-scroll fixed top-0 z-50 flex h-screen w-screen flex-col items-center justify-center bg-black/70 backdrop-blur-[6px] supports-[height:100dvh]:h-dvh"
              onClick={closeModal}
            >
              <button className="absolute right-6 top-6 z-10 md:right-8 md:top-8" onClick={closeModal} type="button">
                <Close role="presentation" aria-label="Close" className="size-12" />
              </button>

              <div
                className="w-full max-w-[1060px] px-4 pt-6 md:px-8 lg:pt-0"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <div
                  data-lenis-prevent
                  key={`dedge-widget-open-${widgetOpenCount}`}
                  className="lorem h-[80vh] items-center justify-center overflow-y-auto rounded bg-transparent md:h-full shortDesktop:h-[90vh]"
                >
                  {isLoadingWidget && (
                    <div className="flex h-full items-center justify-center">
                      <div className="spinner m-auto bg-mountain" />
                    </div>
                  )}
                  {widgetError && <p className="body-04 text-red-700 mb-2">{widgetError}</p>}

                  <div
                    id={`fb-widget-${widgetConfigId}`}
                    className="fb-widget inner-widget h-[80vh] w-full overflow-y-auto rounded bg-transparent md:h-full shortDesktop:h-[90vh] [&_.widgets--wrapper]:w-full"
                    data-fbConfig={widgetConfigId}
                  />
                  <script
                    className="fb-widget-config"
                    data-fbConfig={widgetConfigId}
                    type="application/json"
                    dangerouslySetInnerHTML={{ __html: bookingWidgetConfigJson }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalPortal>
    </>
  );
};

export default DEDGEEmbedModule;
